/**
 * `createApiEvidencePolicySource` — the `metadata-registry-api` *confidence
 * policy* port over the shared `ApiClient`.
 *
 * The confidence policy decides where each domain's floor sits and what happens
 * to a judgement below it (keep / flag / demote / withhold). It has always
 * resolved along `system → customer → org → tenant → team → user`; until this
 * port existed the only write path was an admin editing a whole AppConfig
 * document, so a tenant could not see their own floors, let alone change them.
 *
 * The read returns the whole LADDER — every rung with what it authored, the
 * merged effective section, and a per-knob origin — rather than only what it
 * resolves to. That is the difference between a number and an answer: "0.8" is
 * not actionable, "0.8, inherited from your organisation" is.
 *
 * Canonical `/metadata-evidence-policy` paths are rewritten to
 * `internal/metadata-registry-api` by `RESOURCE_REWRITES`.
 */
import { type ApiClient, unwrapData } from "@authe/fe-commons/api";
import { METADATA_PATHS } from "@authe/fe-commons/metadata-analytics";
import type { LadderTier, ResolvedLadder } from "@authe/fe-commons/evidence";

/** What the server serves alongside the ladder so an editor offers real choices. */
export interface PolicyVocabulary {
  /** Domain ids the platform actually resolves. */
  domains: string[];
  /** `keep` | `flag` | `demote` | `withhold`. */
  actions: string[];
  /** Each domain's behaviour before anyone configured it. */
  default_actions: Record<string, string>;
  /** Scalar knobs (thresholds, corroboration) outside the per-domain channels. */
  knobs: string[];
}

export interface PolicyLadderResponse extends ResolvedLadder {
  vocabulary: PolicyVocabulary;
}

export interface EvidencePolicySource {
  /** The whole ladder for the caller's tenancy + the editing vocabulary. */
  getLadder(): Promise<PolicyLadderResponse>;
  /**
   * What the ladder WOULD resolve to with `section` authored at `tier`.
   *
   * Its own route, never a flag on the save: an operator should be able to see
   * which knobs move before anything is written, and a client bug must not be
   * able to turn one into the other.
   */
  preview(
    tier: LadderTier,
    section: Record<string, unknown>,
  ): Promise<Pick<ResolvedLadder, "effective" | "origins" | "problems">>;
  /** Author this rung's SPARSE slice — only the knobs it sets. */
  save(
    tier: LadderTier,
    section: Record<string, unknown>,
    note?: string,
  ): Promise<void>;
  /** Drop this rung's override; it goes back to inheriting. */
  clear(tier: LadderTier): Promise<void>;
}

export function createApiEvidencePolicySource(
  client: ApiClient,
): EvidencePolicySource {
  const path = METADATA_PATHS.evidencePolicy;
  return {
    async getLadder() {
      return unwrapData(await client.get(path)) as PolicyLadderResponse;
    },
    async preview(tier, section) {
      return unwrapData(
        await client.post(
          METADATA_PATHS.evidencePolicyPreview,
          { section },
          { query: { tier } },
        ),
      ) as Pick<ResolvedLadder, "effective" | "origins" | "problems">;
    },
    async save(tier, section, note) {
      await client.put(path, { section, note }, { query: { tier } });
    },
    async clear(tier) {
      await client.delete(path, { query: { tier } });
    },
  };
}
